/**
 * motion-animations.js
 * Powered by Motion (motion.dev) — the same engine as Framer Motion, no React needed.
 * Covers: scroll-triggered fades, staggered grid reveals, hover lifts on cards,
 *         spring hover on header icons, press feedback on buttons, nav link micro-lifts.
 */

import {
  animate,
  inView,
  stagger,
  hover,
  press,
} from 'https://cdn.jsdelivr.net/npm/motion@11/+esm';

/* ─────────────────────────────────────────────────
   1. INJECT INITIAL-STATE CSS
   Elements are hidden via CSS before JS touches them
   so there is no flash of unstyled content.
───────────────────────────────────────────────── */
(function injectCSS() {
  const s = document.createElement('style');
  s.textContent = `
    [data-m-rise]          { opacity: 0; transform: translateY(30px); }
    [data-m-fade]          { opacity: 0; }
    [data-m-grid] > *      { opacity: 0; transform: translateY(22px); }
    [data-m-grid-h] > *    { opacity: 0; transform: translateX(-18px); }
  `;
  document.head.appendChild(s);
})();

/* ─────────────────────────────────────────────────
   2. HELPERS
───────────────────────────────────────────────── */
const ease  = [0.22, 1, 0.36, 1]; // custom ease-out expo
const spring = { easing: [0.34, 1.56, 0.64, 1], duration: 0.35 }; // spring-like

function isBelowFold(el) {
  return el.getBoundingClientRect().top > window.innerHeight * 0.92;
}

/* ─────────────────────────────────────────────────
   3. MARK ELEMENTS (runs before scroll listeners)
───────────────────────────────────────────────── */
function markElements() {
  /* Sections below the fold rise in */
  document.querySelectorAll(
    '.section-wrapper:not(.hero-wrapper), .shopify-section:not(#shopify-section-header-group)'
  ).forEach(el => {
    if (isBelowFold(el)) el.setAttribute('data-m-rise', '');
  });

  /* Section headings */
  document.querySelectorAll(
    '.section-header, .section-header__heading, .collection-hero__title'
  ).forEach(el => {
    if (isBelowFold(el)) el.setAttribute('data-m-fade', '');
  });

  /* Product / collection grids  */
  document.querySelectorAll(
    '.grid.grid--2-col, .grid.grid--3-col, .grid.grid--4-col, .grid--2-col-tablet, .collection-grid'
  ).forEach(el => {
    el.setAttribute('data-m-grid', '');
    /* remove any section-level rise so grid items are the unit */
    el.closest('[data-m-rise]') && el.closest('[data-m-rise]').removeAttribute('data-m-rise');
  });

  /* Footer columns slide in horizontally */
  document.querySelectorAll('.footer__content-top').forEach(el => {
    el.setAttribute('data-m-grid-h', '');
  });
}

/* ─────────────────────────────────────────────────
   4. SCROLL-TRIGGERED REVEALS
───────────────────────────────────────────────── */
function initScrollReveals() {
  /* Sections rise */
  document.querySelectorAll('[data-m-rise]').forEach(el => {
    inView(
      el,
      () => animate(el, { opacity: 1, y: 0 }, { duration: 0.7, easing: ease }),
      { margin: '0px 0px -80px 0px' }
    );
  });

  /* Headings fade */
  document.querySelectorAll('[data-m-fade]').forEach(el => {
    inView(
      el,
      () => animate(el, { opacity: 1 }, { duration: 0.6, easing: ease }),
      { margin: '0px 0px -60px 0px' }
    );
  });

  /* Grid children stagger */
  document.querySelectorAll('[data-m-grid]').forEach(grid => {
    const children = Array.from(grid.children);
    if (!children.length) return;
    inView(
      grid,
      () =>
        animate(
          children,
          { opacity: 1, y: 0 },
          { duration: 0.55, delay: stagger(0.1, { start: 0 }), easing: ease }
        ),
      { margin: '0px 0px -40px 0px' }
    );
  });

  /* Footer columns slide in */
  document.querySelectorAll('[data-m-grid-h]').forEach(grid => {
    const children = Array.from(grid.children);
    if (!children.length) return;
    inView(
      grid,
      () =>
        animate(
          children,
          { opacity: 1, x: 0 },
          { duration: 0.6, delay: stagger(0.12), easing: ease }
        ),
      { margin: '0px 0px -40px 0px' }
    );
  });
}

/* ─────────────────────────────────────────────────
   5. PRODUCT CARD HOVERS
   Lift the card + subtle zoom on the image
───────────────────────────────────────────────── */
function initCardHovers() {
  document.querySelectorAll('.card-wrapper').forEach(card => {
    const img = card.querySelector('.card__media img');

    hover(card, () => {
      animate(card, { y: -6 }, { duration: 0.4, easing: ease });
      if (img) animate(img, { scale: 1.05 }, { duration: 0.55, easing: ease });
      return () => {
        animate(card, { y: 0 }, { duration: 0.35, easing: 'ease-out' });
        if (img) animate(img, { scale: 1 }, { duration: 0.45, easing: 'ease-out' });
      };
    });
  });
}

/* ─────────────────────────────────────────────────
   6. BUTTON HOVER + PRESS
───────────────────────────────────────────────── */
function initButtonAnimations() {
  const btns = document.querySelectorAll(
    '.button:not(.header-actions__action):not([class*="icon"]), .sw-cta'
  );

  btns.forEach(btn => {
    hover(btn, () => {
      animate(btn, { scale: 1.03 }, { duration: 0.22, easing: 'ease-out' });
      return () => animate(btn, { scale: 1 }, { duration: 0.25, easing: 'ease-out' });
    });

    press(btn, () => {
      animate(btn, { scale: 0.96 }, { duration: 0.08, easing: 'ease-in' });
      return () => animate(btn, { scale: 1 }, { duration: 0.15, easing: ease });
    });
  });
}

/* ─────────────────────────────────────────────────
   7. HEADER ICON HOVERS (cart, account, search)
   Spring bounce — feels snappy and alive
───────────────────────────────────────────────── */
function initIconHovers() {
  document.querySelectorAll('.header-actions__action').forEach(icon => {
    hover(icon, () => {
      animate(icon, { scale: 1.18, y: -2 }, spring);
      return () => animate(icon, { scale: 1, y: 0 }, { duration: 0.28, easing: 'ease-out' });
    });
  });
}

/* ─────────────────────────────────────────────────
   8. NAV LINK MICRO-LIFT
───────────────────────────────────────────────── */
function initNavHovers() {
  document.querySelectorAll('.header__column--left nav a, .header__column--center nav a').forEach(link => {
    hover(link, () => {
      animate(link, { y: -2 }, { duration: 0.18, easing: 'ease-out' });
      return () => animate(link, { y: 0 }, { duration: 0.18, easing: 'ease-out' });
    });
  });
}

/* ─────────────────────────────────────────────────
   9. ANNOUNCEMENT BAR ENTER
───────────────────────────────────────────────── */
function initAnnouncementReveal() {
  const bar = document.querySelector('.announcement-bar, .header-announcements');
  if (!bar) return;
  animate(bar, { opacity: [0, 1], y: [-6, 0] }, { duration: 0.6, easing: ease, delay: 0.3 });
}

/* ─────────────────────────────────────────────────
   10. LOGO HOVER PULSE
───────────────────────────────────────────────── */
function initLogoHover() {
  const logo = document.querySelector('.header-logo__image-container, .header-logo a');
  if (!logo) return;
  hover(logo, () => {
    animate(logo, { scale: 1.04 }, { duration: 0.3, easing: ease });
    return () => animate(logo, { scale: 1 }, { duration: 0.3, easing: 'ease-out' });
  });
}

/* ─────────────────────────────────────────────────
   BOOT
───────────────────────────────────────────────── */
function init() {
  markElements();
  initScrollReveals();
  initCardHovers();
  initButtonAnimations();
  initIconHovers();
  initNavHovers();
  initAnnouncementReveal();
  initLogoHover();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
